import { Head, Link, router } from "@inertiajs/react"
import { useState } from "react"
import Markdown from "react-markdown"
import type Listing from "@/types/serializers/Listing"
import AdminLayout from "@/components/admin/layout"
import ListingMap from "@/components/dashboard/listing-map"
import ImageLightbox from "@/components/dashboard/image-lightbox"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { OptimizedImage } from "@/components/ui/optimized-image"

interface VideoSummary {
  public_id: string
  name: string
  status: string
}

interface CostSummary {
  total_cost_cents: number
  by_job_type: Record<string, number>
  by_provider: Record<string, number>
  by_video: Array<{ name: string; cost_cents: number }>
}

interface Props {
  listing: Listing
  account_name: string
  owner_name: string
  owner_id: number
  videos: VideoSummary[]
  google_maps_key: string | null
  google_maps_map_id?: string | null
  cost_summary: CostSummary
}

const LISTING_TYPE_LABELS: Record<string, string> = {
  sale: "For sale",
  long_let: "Long let",
  short_let: "Short let",
  holiday_let: "Holiday let",
}

const PROPERTY_TYPE_LABELS: Record<string, string> = {
  house: "House",
  flat: "Flat",
  apartment: "Apartment",
  bungalow: "Bungalow",
  cottage: "Cottage",
  studio: "Studio",
  detached: "Detached",
  semi_detached: "Semi-detached",
  terraced: "Terraced",
  maisonette: "Maisonette",
  land: "Land",
  commercial: "Commercial",
  other: "Other",
}

const PROVIDER_COLOURS: Record<string, string> = {
  anthropic: "#D4A853",
  elevenlabs: "#60A5FA",
  fal: "#34D399",
  remotion: "#A78BFA",
  aws: "#F97316",
  kernel: "#F472B6",
  internal: "#6B6B6B",
}

const JOB_TYPE_COLOURS: Record<string, string> = {
  scrape: "#F472B6",
  extract: "#D4A853",
  classify_images: "#D4A853",
  script: "#D4A853",
  voiceover: "#60A5FA",
  music: "#60A5FA",
  enhance_image: "#34D399",
  animate_clip: "#34D399",
  render: "#A78BFA",
  copy_video: "#F97316",
}

const STATUS_COLOURS: Record<string, string> = {
  draft: "bg-amber-500/10 text-amber-400 border-amber-500/20",
  active: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
  archived: "bg-cream/10 text-cream/60 border-cream/20",
}

const VIDEO_STATUS_COLOURS: Record<string, string> = {
  draft: "bg-amber-500/10 text-amber-400 border-amber-500/20",
  scripted: "bg-blue-500/10 text-blue-400 border-blue-500/20",
  rendered: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
  published: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
  archived: "bg-cream/10 text-cream/60 border-cream/20",
}

export default function AdminListingShow({
  listing,
  account_name,
  owner_name,
  owner_id,
  videos,
  google_maps_key,
  google_maps_map_id,
  cost_summary,
}: Props) {
  const isShortLet =
    listing.listing_type === "short_let" || listing.listing_type === "holiday_let"
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null)

  return (
    <AdminLayout>
      <Head title={`${listing.address} | Admin`} />

      {/* Back link */}
      <Link
        href="/admin/listings"
        className="mb-6 inline-flex items-center gap-1 text-sm text-cream/50 hover:text-cream"
      >
        <svg
          className="size-4"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth={1.5}
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5 8.25 12l7.5-7.5" />
        </svg>
        Back to listings
      </Link>

      {/* Header */}
      <div className="mb-8">
        <h1 className="font-display text-2xl font-semibold text-cream">{listing.address}</h1>
        {listing.price && <p className="mt-1 text-lg font-semibold text-cream/80">{listing.price}</p>}
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <Badge className={STATUS_COLOURS[listing.status]}>{listing.status}</Badge>
          <Badge className="border-white/10 bg-white/5 text-cream/70">
            {LISTING_TYPE_LABELS[listing.listing_type]}
          </Badge>
        </div>
      </div>

      {/* Account / Owner card */}
      <div className="mb-6 rounded-lg border border-white/[0.06] bg-white/[0.02] p-4">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="space-y-1 text-sm">
            <p className="text-cream/40">
              Account: <span className="text-cream/70">{account_name}</span>
            </p>
            <p className="text-cream/40">
              Owner: <span className="text-cream/70">{owner_name}</span>
            </p>
          </div>
          <Button
            size="sm"
            variant="outline"
            className="border-white/20 text-cream hover:bg-white/10"
            onClick={() => router.post(`/admin/users/${owner_id}/impersonate`)}
          >
            Impersonate
          </Button>
        </div>
      </div>

      {/* Image gallery */}
      {listing.images_data.length > 0 && (
        <div className="mb-6 overflow-hidden rounded-lg border border-white/[0.06]">
          <div className="grid grid-cols-2 gap-1 sm:grid-cols-3 lg:grid-cols-4">
            {listing.images_data.map((img, i) => (
              <button
                key={img.signed_id}
                type="button"
                onClick={() => setLightboxIndex(i)}
                className={`cursor-pointer overflow-hidden bg-white/5 ${i === 0 ? "col-span-2 row-span-2" : ""}`}
              >
                <OptimizedImage
                  src={img.thumb_url}
                  alt={`${listing.address}, photo ${i + 1}`}
                  className="size-full object-cover transition-transform duration-200 hover:scale-105"
                />
              </button>
            ))}
          </div>
        </div>
      )}

      {lightboxIndex !== null && (
        <ImageLightbox
          images={listing.images_data}
          initialIndex={lightboxIndex}
          listingSlug={listing.slug}
          onClose={() => setLightboxIndex(null)}
        />
      )}

      {/* Property details grid */}
      <div className="mb-6 rounded-lg border border-white/[0.06] bg-white/[0.02] p-6">
        <h2 className="mb-4 text-sm font-medium uppercase tracking-wider text-cream/40">
          Property details
        </h2>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {listing.property_type && (
            <DetailItem
              label="Type"
              value={PROPERTY_TYPE_LABELS[listing.property_type] || listing.property_type}
            />
          )}
          <DetailItem
            label="Listing type"
            value={LISTING_TYPE_LABELS[listing.listing_type]}
          />
          {listing.bedrooms != null && (
            <DetailItem label="Bedrooms" value={listing.bedrooms.toString()} />
          )}
          {listing.bathrooms != null && (
            <DetailItem label="Bathrooms" value={listing.bathrooms.toString()} />
          )}
        </div>
      </div>

      {/* Description */}
      {listing.description && (
        <div className="mb-6 rounded-lg border border-white/[0.06] bg-white/[0.02] p-6">
          <h2 className="mb-4 text-sm font-medium uppercase tracking-wider text-cream/40">
            Description
          </h2>
          <div className="prose prose-sm prose-invert max-w-none text-cream/70 prose-headings:text-white prose-strong:text-cream/80">
            <Markdown>{listing.description}</Markdown>
          </div>
        </div>
      )}

      {/* Features */}
      {listing.features.length > 0 && (
        <div className="mb-6 rounded-lg border border-white/[0.06] bg-white/[0.02] p-6">
          <h2 className="mb-4 text-sm font-medium uppercase tracking-wider text-cream/40">
            Features
          </h2>
          <div className="flex flex-wrap gap-2">
            {listing.features.map((feature) => (
              <Badge
                key={feature}
                className="border-white/10 bg-white/5 text-cream/70"
              >
                {feature}
              </Badge>
            ))}
          </div>
        </div>
      )}

      {/* Short-let details */}
      {isShortLet &&
        (listing.nightly_rate || listing.min_nights || listing.availability_url) && (
          <div className="mb-6 rounded-lg border border-white/[0.06] bg-white/[0.02] p-6">
            <h2 className="mb-4 text-sm font-medium uppercase tracking-wider text-cream/40">
              Short-let details
            </h2>
            <div className="grid gap-4 sm:grid-cols-3">
              {listing.nightly_rate && (
                <DetailItem label="Nightly rate" value={`£${listing.nightly_rate}`} />
              )}
              {listing.min_nights && (
                <DetailItem label="Minimum nights" value={listing.min_nights.toString()} />
              )}
              {listing.availability_url && (
                <div>
                  <p className="text-xs text-cream/40">Availability</p>
                  <a
                    href={listing.availability_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-1 text-sm text-blue-400 hover:underline"
                  >
                    View availability
                  </a>
                </div>
              )}
            </div>
          </div>
        )}

      {/* Location map */}
      {listing.latitude && listing.longitude && (
        <div className="mb-6">
          <ListingMap
            latitude={Number(listing.latitude)}
            longitude={Number(listing.longitude)}
            address={listing.address}
            apiKey={google_maps_key}
            mapId={google_maps_map_id}
          />
        </div>
      )}

      {/* Source URL */}
      {listing.source_url && (
        <div className="mb-6 rounded-lg border border-white/[0.06] bg-white/[0.02] p-6">
          <h2 className="mb-4 text-sm font-medium uppercase tracking-wider text-cream/40">
            Source
          </h2>
          <a
            href={listing.source_url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-sm text-blue-400 hover:underline"
          >
            View original listing
            <ExternalLinkIcon className="size-4" />
          </a>
        </div>
      )}

      {/* Cost breakdown */}
      <div className="mb-6 rounded-lg border border-white/[0.06] bg-white/[0.02] p-6">
        <h2 className="mb-4 text-sm font-medium uppercase tracking-wider text-cream/40">
          Cost breakdown
        </h2>
        <p className="mb-4 text-2xl font-semibold tabular-nums text-cream">
          {formatCurrency(cost_summary.total_cost_cents)}
        </p>
        {cost_summary.total_cost_cents > 0 ? (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <CostBreakdown title="By job type" data={cost_summary.by_job_type} colours={JOB_TYPE_COLOURS} />
            <CostBreakdown title="By provider" data={cost_summary.by_provider} colours={PROVIDER_COLOURS} />
            {cost_summary.by_video.length > 0 && (
              <ListCostBreakdown title="By video" items={cost_summary.by_video} />
            )}
          </div>
        ) : (
          <p className="text-sm text-cream/30">No costs recorded.</p>
        )}
      </div>

      {/* Associated videos */}
      <div className="mb-6 rounded-lg border border-white/[0.06] bg-white/[0.02] p-6">
        <h2 className="mb-4 text-sm font-medium uppercase tracking-wider text-cream/40">
          Videos ({videos.length})
        </h2>
        {videos.length === 0 ? (
          <p className="text-sm text-cream/30">No videos created for this listing.</p>
        ) : (
          <div className="space-y-2">
            {videos.map((video) => (
              <Link
                key={video.public_id}
                href={`/admin/videos/${video.public_id}`}
                className="flex items-center justify-between rounded-md border border-white/[0.04] bg-white/[0.02] px-4 py-3 transition-colors hover:bg-white/[0.06]"
              >
                <span className="text-sm text-cream/70">{video.name}</span>
                <Badge className={VIDEO_STATUS_COLOURS[video.status] || VIDEO_STATUS_COLOURS.draft}>
                  {video.status}
                </Badge>
              </Link>
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  )
}

function DetailItem({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-xs text-cream/40">{label}</p>
      <p className="mt-1 text-sm font-medium text-cream/80">{value}</p>
    </div>
  )
}

function CostBreakdown({ title, data, colours }: { title: string; data: Record<string, number>; colours: Record<string, string> }) {
  const entries = Object.entries(data)
    .map(([name, value]) => ({ name, value }))
    .sort((a, b) => b.value - a.value)
  const total = entries.reduce((sum, e) => sum + e.value, 0)

  return (
    <div>
      <p className="mb-2 text-xs font-medium uppercase tracking-wider text-cream/30">{title}</p>
      <div className="space-y-1.5">
        {entries.map((entry) => {
          const pct = total > 0 ? (entry.value / total) * 100 : 0
          const colour = colours[entry.name] || "#6B6B6B"
          return (
            <div key={entry.name}>
              <div className="mb-0.5 flex items-center justify-between text-xs">
                <span className="text-cream/60">{formatLabel(entry.name)}</span>
                <span className="tabular-nums text-cream/40">{formatCurrency(entry.value)}</span>
              </div>
              <div className="h-1 overflow-hidden rounded-full bg-white/[0.06]">
                <div
                  className="h-full rounded-full transition-all"
                  style={{ width: `${pct}%`, backgroundColor: colour }}
                />
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

function ListCostBreakdown({
  title,
  items,
}: {
  title: string
  items: Array<{ name: string; cost_cents: number }>
}) {
  const total = items.reduce((sum, e) => sum + e.cost_cents, 0)

  return (
    <div>
      <p className="mb-2 text-xs font-medium uppercase tracking-wider text-cream/30">{title}</p>
      <div className="space-y-1.5">
        {items.map((item) => {
          const pct = total > 0 ? (item.cost_cents / total) * 100 : 0
          return (
            <div key={item.name}>
              <div className="mb-0.5 flex items-center justify-between text-xs">
                <span className="max-w-[70%] truncate text-cream/60">{item.name}</span>
                <span className="tabular-nums text-cream/40">{formatCurrency(item.cost_cents)}</span>
              </div>
              <div className="h-1 overflow-hidden rounded-full bg-white/[0.06]">
                <div
                  className="h-full rounded-full bg-red transition-all"
                  style={{ width: `${pct}%` }}
                />
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

function formatCurrency(cents: number): string {
  const dollars = cents / 100
  if (dollars >= 1) return `$${dollars.toFixed(2)}`
  return `${cents}c`
}

function formatLabel(key: string): string {
  return key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
}

function ExternalLinkIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M13.5 6H5.25A2.25 2.25 0 0 0 3 8.25v10.5A2.25 2.25 0 0 0 5.25 21h10.5A2.25 2.25 0 0 0 18 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25"
      />
    </svg>
  )
}
